import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Layout/Navbar';
import LoginForm from './components/Auth/LoginForm';
import RegisterForm from './components/Enterprise/RegisterForm';
import AdminLayoutClean from './components/Dashboard/AdminLayoutClean';
import EntrepriseLayout from './components/Dashboard/EntrepriseLayout';
import EmployeLayout from './components/Layout/EmployeLayout';
import EmployeDashboard from './components/Dashboard/EmployeDashboard';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CGU from './pages/CGU';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import './App.css';

// Composant pour les routes protégées
const ProtectedRoute = ({ children, allowedUserTypes }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedUserTypes && !allowedUserTypes.includes(user.user_type)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Accès non autorisé</h2>
          <p className="text-gray-600 mb-6">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
          <p className="text-sm text-gray-500">
            Type d'utilisateur requis: {allowedUserTypes.join(', ')}
          </p>
        </div>
      </div>
    );
  }

  return children;
};

// Composant principal de l'application
const AppContent = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initialisation de l'application...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gray-50">
        {/* Navbar seulement pour les employés (pas pour admin ALO ni entreprises) */}
        {user && user.user_type === 'EMPLOYE' && <Navbar />}
        
        <Routes>
          {/* Route de connexion */}
          <Route 
            path="/login" 
            element={
              user ? (
                <Navigate to="/" replace />
              ) : (
                <LoginForm />
              )
            } 
          />

          {/* Route d'inscription entreprise */}
          <Route 
            path="/register" 
            element={
              user ? (
                <Navigate to="/" replace />
              ) : (
                <RegisterForm />
              )
            } 
          />

          {/* Routes de réinitialisation de mot de passe */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Routes publiques - CGU et Politique de confidentialité */}
          <Route path="/cgu" element={<CGU />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />

          {/* Routes protégées pour admin ALO */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute allowedUserTypes={['ALO_ADMIN']}>
                <AdminLayoutClean />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute allowedUserTypes={['ALO_ADMIN']}>
                <AdminLayoutClean />
              </ProtectedRoute>
            } 
          />

          {/* Routes protégées pour entreprises */}
          <Route 
            path="/entreprise" 
            element={
              <ProtectedRoute allowedUserTypes={['ENTREPRISE']}>
                <EntrepriseLayout />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/entreprise/*" 
            element={
              <ProtectedRoute allowedUserTypes={['ENTREPRISE']}>
                <EntrepriseLayout />
              </ProtectedRoute>
            } 
          />

          {/* Route pour employés */}
          <Route 
            path="/employe" 
            element={
              <ProtectedRoute allowedUserTypes={['EMPLOYE']}>
                <EmployeLayout>
                  <EmployeDashboard />
                </EmployeLayout>
              </ProtectedRoute>
            } 
          />

          {/* Route principale : redirection selon le type d'utilisateur */}
          <Route 
            path="/" 
            element={
              user ? (
                user.user_type === 'ALO_ADMIN' ? (
                  <Navigate to="/admin" replace />
                ) : user.user_type === 'ENTREPRISE' ? (
                  <Navigate to="/entreprise" replace />
                ) : user.user_type === 'EMPLOYE' ? (
                  <Navigate to="/employe" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />

          {/* Route par défaut : redirection selon le type d'utilisateur */}
          <Route 
            path="*" 
            element={
              user ? (
                user.user_type === 'ALO_ADMIN' ? (
                  <Navigate to="/admin" replace />
                ) : user.user_type === 'ENTREPRISE' ? (
                  <Navigate to="/entreprise" replace />
                ) : user.user_type === 'EMPLOYE' ? (
                  <Navigate to="/employe" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        </Routes>
      </div>
    </BrowserRouter>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;